import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { formatFileType } from '@/utils/file';
import { AstroFile } from '@/models/AstroFile';
import { useEffect, useState } from 'react';
import FileCard from './FileCard';
import FileInputZone from './FileInputZone';

type FileUploaderProps = {
  value?: File | string;
  label?: string;
  fileName?: string;
  limit?: number;
  accept?: string;
  testId?: string;
  onChange: (file?: File | null) => void;
};

const SingleFileUploader = ({
  value,
  limit,
  onChange,
  accept,
  label,
  fileName,
  testId,
}: FileUploaderProps) => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [astroFile, setAstroFile] = useState<AstroFile | undefined>(undefined);

  const getUrl = () => {
    if (typeof value === 'string') {
      return value;
    }

    if (file) {
      return URL.createObjectURL(file);
    }

    return '';
  };

  const createFileFromBlob = (
    blob: Blob,
    fileName: string = 'Documentação do projeto',
  ) => {
    const fileType = formatFileType(blob.type);
    const filenameWithExt = `${fileName}.${fileType}`;
    return new File([blob], filenameWithExt, { type: blob.type });
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;

    const uploadedFile = files[0];

    const newFile = createFileFromBlob(
      uploadedFile,
      fileName || uploadedFile.name,
    );

    const newAstroFile: AstroFile = {
      file: newFile,
      fileName: newFile.name,
      fileKey: `local-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      createdAt: new Date().toISOString(),
    };

    onChange(newFile);
    setFile(newFile);
    setAstroFile(newAstroFile);
  };

  const onRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
    setFile(undefined);
    setAstroFile(undefined);
  };

  const url = getUrl();

  useEffect(() => {
    if (!value) return;

    if (value instanceof File) {
      const newAstroFile: AstroFile = {
        file: value,
        fileName: value.name,
        fileKey: `local-${Date.now()}-${Math.random()
          .toString(36)
          .substring(7)}`,
        createdAt: new Date().toISOString(),
      };
      setFile(value);
      setAstroFile(newAstroFile);
      return;
    }

    const urlFileName = fileName || value.split('/').pop() || 'arquivo';
    const newAstroFile: AstroFile = {
      file: value,
      fileName: urlFileName,
      fileKey: `server-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setFile(undefined);
    setAstroFile(newAstroFile);
  }, [value, fileName]);

  return (
    <div className="relative h-16 w-full">
      <ConditionalRenderer condition={!astroFile}>
        <FileInputZone
          onChange={handleFileUpload}
          label={label}
          testId={testId}
          accept={accept}
          limit={limit}
          className={{
            base: 'w-full h-full bg-base-100',
            section: 'min-h-16 p-3',
          }}
        />
      </ConditionalRenderer>

      <ConditionalRenderer condition={astroFile}>
        <div className="w-full">
          {astroFile && (
            <FileCard file={astroFile} onRemove={onRemove} url={url} />
          )}
        </div>
      </ConditionalRenderer>
    </div>
  );
};

export default SingleFileUploader;
